/**
 * Table 11 — advances received and adjusted (FR-E11, e2e-filing B-04), in the words the
 * Returns screen shows.
 *
 * The service decides everything: which entries book (`gstr1.advance_route`), the (POS,
 * rate) rows, and the month's standing — `none_declared`, `held` or `booked` — which it
 * serves as `advances` on every tables read. A month with no advances is a STATE here, never
 * a zero: an empty tab would read as "nil", and the service's own sentence for the absence
 * is what this prints instead.
 */

import type { AdvanceEntry, AdvancesState, PeriodRecord } from './api'

/** The sentence under the section tabs when Table 11 has nothing to show. */
export function advancesStateWords(advances: AdvancesState | undefined): string | null {
  switch (advances?.state) {
    case 'none_declared':
      return (
        'No advances were declared this month: no advances register was uploaded and none ' +
        'was entered. Table 11 is absent, not nil.'
      )
    case 'held':
      return (
        `${advances.entries} advance ${advances.entries === 1 ? 'entry is' : 'entries are'} ` +
        'held for review, so Table 11 carries nothing yet — see the gates panel.'
      )
    default:
      return null
  }
}

/**
 * Which return can take a typed advance: a GSTR-1 that is not filed or locked (a filed
 * month's advance is an amendment, FR-31) and not a snapshot (never recomputed, ND-3).
 */
export function canEnterAdvance(row: Pick<PeriodRecord, 'state' | 'origin' | 'return_type'>): boolean {
  return (
    row.return_type === 'GSTR1' && row.origin !== 'snapshot' && !['filed', 'locked'].includes(row.state)
  )
}

/** What a typed entry recorded, as one sentence. */
export function advanceEntryWords(result: Pick<AdvanceEntry, 'words' | 'demoted' | 'state'>): string {
  const head = `Entered: ${result.words}.`
  if (result.demoted) {
    return `${head} The return is back at Prepared — send it for approval again.`
  }
  return `${head} It is in Table 11 of this month's draft.`
}
